package fyi.moko.hotpl.ui.placeform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement

/**
 * JS/web actual: the same `<input type="file">` as the picker, plus `capture="environment"`,
 * which asks a mobile browser to open the rear camera directly instead of the file
 * chooser. Desktop browsers ignore the hint and fall back to a normal file dialog.
 *
 * [isAvailable] is therefore always true — there is no reliable way to ask a browser
 * whether `capture` will actually reach a camera, and the fallback is harmless (the user
 * gets the file chooser, which is what the picker would have given them anyway). Hiding
 * the affordance on a guess would be worse than a graceful degrade.
 *
 * Bytes go through [processPickedFile], so the downscale and JPEG re-encode match the
 * picker — which matters more here than on mobile, since `JsPlaceImageStore` has to fit
 * inside `localStorage`'s few-MB-per-origin budget.
 */
@Composable
actual fun rememberImageCapturer(): ImageCapturer {
    val scope = rememberCoroutineScope()
    return object : ImageCapturer {
        override val isAvailable = true

        override fun capture(onResult: (ByteArray?) -> Unit) {
            val input = document.createElement("input") as HTMLInputElement
            input.type = "file"
            input.accept = "image/*"
            // No typed property for this on HTMLInputElement — set the attribute directly.
            input.setAttribute("capture", "environment")
            input.onchange = {
                val file = input.files?.item(0)
                if (file != null) {
                    scope.launch { onResult(processPickedFile(file)) }
                } else {
                    onResult(null)
                }
                Unit
            }
            input.click()
        }
    }
}
